import React, { useEffect, useState, useRef } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { zTouch as theme } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Paper,
  Container,
  Card,
  CardContent,
  Divider,
  CardHeader,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormLabel,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { saveAs } from "file-saver";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";

const genAI = new GoogleGenerativeAI("AIzaSyCsr7-T_IMH_aeCBB-xGwW6QTQHeJDF-VE");
const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash-001" });

const SchemaForm = () => {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState(localStorage.getItem("title") || "");
  const [description, setDescription] = useState(
    localStorage.getItem("description") || ""
  );
  const [fields, setFields] = useState(
    JSON.parse(localStorage.getItem("fields")) || [
      { label: "id", type: "string" },
    ]
  );
  const [dummyData, setDummyData] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("json");
  const [copied, setCopied] = useState(false);
  const [save, setSave] = useState(false);
  const textareaRef = useRef(null);

  const naviage = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    if (title && description) setDisabled(false);
    // Save data to local storage on every change
    localStorage.setItem("title", title);
    localStorage.setItem("description", description);
    localStorage.setItem("fields", JSON.stringify(fields));
  }, [title, description, fields]);

  const handleNextStep = () => {
    if (title && description) {
      setStep((prevStep) => prevStep + 1);
    } else {
      alert("Please enter a title and description before proceeding.");
    }
  };

  const handleAddField = (event) => {
    event.preventDefault();
    setFields([...fields, { label: "", type: "string" }]);
  };

  const handleFieldChange = (index, key, value) => {
    const newFields = [...fields];
    newFields[index][key] = value;
    setFields(newFields);
  };

  const handleAddObjectField = (objectIndex, event) => {
    event.preventDefault();
    const newFields = [...fields];
    newFields[objectIndex].objectFields = [
      ...(newFields[objectIndex].objectFields || []),
      { label: "", type: "string" },
    ];
    setFields(newFields);
  };

  const handleObjectFieldChange = (objectIndex, fieldIndex, key, value) => {
    const newFields = [...fields];
    newFields[objectIndex].objectFields[fieldIndex][key] = value;
    setFields(newFields);
  };

  const handleRemoveObjectField = (objectIndex, fieldIndex, event) => {
    event.preventDefault();
    const newFields = [...fields];
    newFields[objectIndex].objectFields.splice(fieldIndex, 1);
    setFields(newFields);
  };

  const handleRemoveField = (fieldIndex, event) => {
    event.preventDefault();
    const newFields = [...fields];
    newFields.splice(fieldIndex, 1);
    setFields(newFields);
  };

  const handleTypeChange = (index, value) => {
    const newFields = [...fields];
    newFields[index].type = value;

    if (value === "object") {
      newFields[index].objectFields = [
        ...(newFields[index].objectFields || []),
        { label: "", type: "string" },
      ];
    } else {
      newFields[index].objectFields = [];
    }

    setFields(newFields);
  };

  const generateDummyData = async () => {
    const prompt = `Generate 30 random dummy data which makes sense or relate with the title entries based on the following schema:
      Title: ${title}
      Description: ${description}
      Fields: 
        ${fields.map((field) => {
          if (field.type === "object") {
            return `
              ${field.label}: {
                ${field.objectFields.map(
                  (objectField) => `${objectField.label}: ${objectField.type}`
                )}
              }`;
          } else {
            return `${field.label}: ${field.type}`;
          }
        })}
      Include an id field for each entry.
      For image generation always use all this websites in one json generation dummyimage.com, unsplash.com and pexels.com.
      Just respond with only Json nothing else, this should be the response: 
      [ 
        {
         "id": "",
        }
      ]`;

    setLoading(true);
    try {
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const parsedData = response.text;

      setDummyData(parsedData);
      setLoading(false);
      setStep(3);
    } catch (error) {
      setLoading(false);
      console.error("Error generating dummy data:", error);
    }
  };

  const handleDownload = async () => {
    let dataToDownload = dummyData?.replace(/```json|```/g, "");

    if (downloadFormat === "mysql") {
      // Convert JSON to MySQL insert statements
      const data = JSON.parse(dummyData?.replace(/```json|```/g, ""));
      const tableName = title.replace(/[^a-zA-Z0-9]/g, "_"); // Replace spaces and special characters
      const columns = Object.keys(data[0]).join(", ");
      dataToDownload = `INSERT INTO ${tableName} (${columns}) VALUES\n`;
      dataToDownload += data
        .map(
          (row) =>
            `(${Object.values(row)
              .map((value) => `'${value}'`)
              .join(", ")})`
        )
        .join(",\n");
    } else if (downloadFormat === "postgresql") {
      // Convert JSON to PostgreSQL insert statements
      const data = JSON.parse(dummyData?.replace(/```json|```/g, ""));
      const tableName = title.replace(/[^a-zA-Z0-9]/g, "_"); // Replace spaces and special characters
      const columns = Object.keys(data[0]).join(", ");
      dataToDownload = `INSERT INTO ${tableName} (${columns}) VALUES\n`;
      dataToDownload += data
        .map(
          (row) =>
            `(${Object.values(row)
              .map((value) => `'${value}'`)
              .join(", ")})`
        )
        .join(",\n");
    } else if (downloadFormat === "mongodb") {
      // Convert JSON to MongoDB insert statements
      const data = JSON.parse(dummyData?.replace(/```json|```/g, ""));
      const tableName = title.replace(/[^a-zA-Z0-9]/g, "_"); // Replace spaces and special characters
      dataToDownload = `db.${tableName}.insertMany([`;
      dataToDownload += data.map((row) => JSON.stringify(row)).join(",\n");
      dataToDownload += "])";
    }

    const blob = new Blob([dataToDownload], { type: "text/plain" });
    saveAs(blob, `${title}.${downloadFormat}`);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(dummyData?.replace(/```json|```/g, ""));
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  async function saveData() {
    try {
      setSave(true);
      const formattedData = JSON.parse(dummyData.replace(/```json|```/g, ""));
      const token = await getToken();

      await axios.post(
        "https://faker-2yvr.onrender.com/api/items",
        {
          title: title,
          description: description,
          schema: fields,
          jsonData: formattedData,
          creator: user.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSave(false);
      naviage("/dashboard");
    } catch (error) {
      setSave(false);
      console.error("Error formatting data:", error);
    }
  }

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleNaviage = () => {
    naviage("/dashboard");
  };
  return (
    <Container maxWidth="xl" sx={{ padding: "20px", margin: "auto" }}>
      <Stepper sx={{ my: 3 }} activeStep={step - 1}>
        <Step key={1}>
          <StepLabel>Define Your Schema</StepLabel>
        </Step>
        <Step key={2}>
          <StepLabel>Generate Dummy Data</StepLabel>
        </Step>
        <Step key={3}>
          <StepLabel>Generated Data</StepLabel>
        </Step>
      </Stepper>
      {step === 1 && (
        <Grid sx={{ minHeight: 400 }} container spacing={3}>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              width: "95%",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
              boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
              backgroundColor: "white",
              zIndex: 999,
              borderRadius: "15px",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleBack}
              disabled={true}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Back"
              )}
            </Button>
            <Button
              variant="contained"
              onClick={handleNextStep}
              disabled={disabled}
            >
              Next
            </Button>
          </Box>
          <Grid item xs={12} md={12} sx={{ marginBottom: "5rem" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "70vh",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ marginBottom: "20px" }}>
                <Typography variant="h5" gutterBottom>
                  Schema Information
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Please provide the following details for your data schema:
                </Typography>
              </Box>

              <Card
                sx={{
                  padding: "20px",
                  marginBottom: "20px",
                  minHeight: 400,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <div>
                    <FormLabel color="primary">
                      Title: (Enter a descriptive title for your data)
                    </FormLabel>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      sx={{ marginBottom: "20px" }}
                      placeholder="e.g., E-commerce Products"
                    />
                  </div>
                  <div>
                    <FormLabel color="primary">
                      Description: (Provide a brief explanation of your data)
                    </FormLabel>
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline
                      rows={4}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      sx={{ marginBottom: "20px" }}
                      placeholder="e.g., Fake e-commerce products for a grocery project"
                    />
                  </div>
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      )}
      {step === 2 && (
        <Grid sx={{ minHeight: 400 }} container spacing={3}>
          {/* Left Column - Input Form */}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              width: "95%",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
              boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
              backgroundColor: "white",
              zIndex: 999,
              borderRadius: "15px",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleBack}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Back"
              )}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={generateDummyData}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Generate Dummy Data"
              )}
            </Button>
          </Box>

          <Grid item xs={12} md={12} sx={{ marginBottom: "5rem" }}>
            <Divider sx={{ marginY: 1 }} />

            <Card
              sx={{
                minHeight: "60vh",
              }}
            >
              <CardContent>
                <CardHeader
                  subheader="Edit the schema as needed"
                  title="Schema Creation"
                />

                <Divider sx={{ marginY: 1 }} />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Define your data fields:
                  </Typography>
                  {fields.map((field, index) => (
                    <Box key={index} sx={{ marginBottom: "20px" }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={1}>
                          <p>{index + 1}. </p>
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Field Label"
                            value={field.label}
                            onChange={(e) =>
                              handleFieldChange(index, "label", e.target.value)
                            }
                            disabled={loading}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            fullWidth
                            select
                            label="Field Type"
                            value={field.type}
                            onChange={(e) =>
                              handleTypeChange(index, e.target.value)
                            }
                            disabled={loading}
                          >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="image">Image</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="object">Object</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            color="error"
                            onClick={(e) => handleRemoveField(index, e)}
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>

                      {field.type === "object" && (
                        <Box
                          sx={{
                            marginTop: "10px",
                            padding: "10px",
                            backgroundColor: "#f5f5f5",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography variant="subtitle1" gutterBottom>
                            Object Fields
                          </Typography>
                          {field.objectFields?.map(
                            (objectField, fieldIndex) => (
                              <Grid container spacing={2} key={fieldIndex}>
                                <Grid item xs={6}>
                                  <TextField
                                    fullWidth
                                    label="Object Field Label"
                                    value={objectField.label}
                                    onChange={(e) =>
                                      handleObjectFieldChange(
                                        index,
                                        fieldIndex,
                                        "label",
                                        e.target.value
                                      )
                                    }
                                    disabled={loading}
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <TextField
                                    fullWidth
                                    select
                                    label="Type"
                                    value={objectField.type}
                                    onChange={(e) =>
                                      handleObjectFieldChange(
                                        index,
                                        fieldIndex,
                                        "type",
                                        e.target.value
                                      )
                                    }
                                    disabled={loading}
                                  >
                                    <MenuItem value="string">String</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="image">Image</MenuItem>
                                  </TextField>
                                </Grid>
                                <Grid item xs={2}>
                                  <IconButton
                                    color="error"
                                    onClick={(e) =>
                                      handleRemoveObjectField(
                                        index,
                                        fieldIndex,
                                        e
                                      )
                                    }
                                    disabled={loading}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            )
                          )}
                          <Button
                            startIcon={<AddCircleIcon />}
                            onClick={(e) => handleAddObjectField(index, e)}
                            sx={{ marginTop: "10px" }}
                            disabled={loading}
                          >
                            Add Object Field
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                </CardContent>
                <Divider sx={{ marginY: 1 }} />

                <Button
                  startIcon={<AddCircleIcon />}
                  onClick={handleAddField}
                  disabled={loading}
                >
                  Add Field
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {step === 3 && (
        <Grid sx={{ minHeight: 400 }} container spacing={3}>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              width: "95%",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
              boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
              backgroundColor: "white",
              zIndex: 999,
              borderRadius: "15px",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleBack}
              disabled={disabled}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNaviage}
              disabled={disabled}
            >
              Dahshboard
            </Button>
          </Box>
          <Grid item xs={12} md={12} sx={{ marginBottom: "5rem" }}>
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 100,
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
                marginTop: "10px",
                padding: "15px",
                backgroundColor: "white",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "flex-start" }}>
                <FormControl fullWidth sx={{ m: 0.5, maxWidth: 100 }}>
                  <InputLabel id="download-format-label">Format</InputLabel>
                  <Select
                    labelId="download-format-label"
                    id="download-format"
                    value={downloadFormat}
                    onChange={(e) => setDownloadFormat(e.target.value)}
                    label="Format"
                    size="small"
                  >
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="mysql">MySQL</MenuItem>
                    <MenuItem value="postgresql">PostgreSQL</MenuItem>
                    <MenuItem value="mongodb">MongoDB</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={handleDownload}
                  size="small"
                  sx={{ padding: "4px 8px", ml: 1 }}
                >
                  <ArrowDownwardIcon
                    sx={{ fontSize: "16px", marginRight: "4px" }}
                  />
                  Download
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "flex-end" }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={save}
                  onClick={() => saveData()}
                  size="small"
                  sx={{ padding: "4px 8px", mr: 3 }}
                >
                  <SyncAltIcon sx={{ fontSize: "16px", marginRight: "4px" }} />
                  Make it API
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCopy}
                  disabled={loading}
                  size="small"
                  sx={{ padding: "4px 8px" }}
                >
                  <ContentCopyIcon
                    sx={{ fontSize: "16px", marginRight: "4px" }}
                  />
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </Box>
            </Box>

            <Card>
              {dummyData && (
                <Paper
                  sx={{
                    padding: "20px",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Generated Dummy Data:
                  </Typography>

                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={() => textareaRef.current?.focus()}
                    onClick={() => textareaRef.current?.focus()}
                    className="relative flex bg-[#282a36]"
                  >
                    <SyntaxHighlighter
                      language="json"
                      style={theme}
                      wrapLines={true}
                      wrapLongLines={true}
                    >
                      {dummyData?.replace(/```json|```/g, "")}
                    </SyntaxHighlighter>
                  </div>
                </Paper>
              )}
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default SchemaForm;
